import { DayObject } from "@/types/date"
import { isPlaceRef, PlaceRef } from "@/types/entity"
import {
  HotelAvailability,
  HotelAvailabilityResponse,
  RankedHotelAvailabilityResponse,
  RankedHotelAvailabilityResult,
} from "@/types/hotels/hotels"
import { Room, TravelerGroup } from "@/types/trip"
import type { QueryCacheKey } from "@reduxjs/toolkit/query"

import type { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { ensureArray } from "@/lib/utils/collections"
import { baseApi } from "./base-api"

// Sent on ranked_availability to opt into auto-alert creation. Stored as the trigger tag on the alert.
export type PriceAlertIntent = "rooms_browsing" | "quote_rooms_browsing"

export interface GetHotelAvailabilityArgs {
  placeRefs: PlaceRef | PlaceRef[]
  travelers: TravelerGroup | undefined
  room?: Room
  start: DayObject
  end: DayObject
  source?: string
  currency?: string
}

export interface GetHotelAvailabilityBatchArgs {
  placeRefs: PlaceRef[]
  travelers: TravelerGroup
  room?: Room
  start: DayObject
  end: DayObject
  source?: string
  currency?: string
}

export interface GetRankedHotelAvailabilityArgs {
  placeRef: PlaceRef
  selectedRoomId?: number
  hotelQuoteRef?: string
  travelers?: TravelerGroup
  childAges?: number[]
  start?: DayObject
  end?: DayObject
  currency?: string
  recoveryToken?: string
  priceAlertIntent?: PriceAlertIntent
}

export const hotelApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHotelAvailability: builder.query<
      HotelAvailabilityResponse,
      GetHotelAvailabilityArgs
    >({
      // Exclude `source` from the cache key so different call sites share
      // the same cached result for identical query parameters.
      serializeQueryArgs: ({ queryArgs }) => {
        const { source: _source, ...rest } = queryArgs
        return JSON.stringify(rest)
      },
      query: ({ placeRefs, travelers, room, start, end, source, currency }) => {
        const placeRefsArray = ensureArray(placeRefs)
        return {
          url: "/api/hotels/availability",
          method: "post",
          data: {
            ...travelers,
            ...(room ? { rooms: [room] } : {}),
            check_in: start,
            check_out: end,
            entity_refs: placeRefsArray,
            source,
            currency,
          },
        }
      },
      transformResponse: (response: HotelAvailabilityResponse) =>
        response || {},
      providesTags: (_result, _error, { placeRefs }) => {
        const placeRefsArray = ensureArray(placeRefs)
        const sortedPlaceRefsArray = [...placeRefsArray].sort()
        // Only provide individual tags - batch results are discarded
        return sortedPlaceRefsArray.map((placeRef) => ({
          type: "HotelAvailability" as const,
          id: placeRef,
        }))
      },
      // in the batch endpoints onQueryStarted or after the query:
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const placeRefsArray = ensureArray(arg.placeRefs)

        // Only process batch queries (multiple placeRefs) to prevent infinite loop
        // Single placeRef queries should not trigger cache population
        if (placeRefsArray.length <= 1) {
          return
        }

        try {
          const result = await queryFulfilled
          const data = result?.data

          // Runtime validation: ensure data is HotelAvailabilityResponse
          if (!data || typeof data !== "object" || Array.isArray(data)) {
            console.error(
              "Invalid HotelAvailabilityResponse: data is not an object",
              data
            )
            return
          }

          // Validate that all keys are PlaceRefs and values are HotelAvailability
          const entries = Object.entries(data)

          for (const [key, value] of entries) {
            if (!isPlaceRef(key)) {
              console.error(
                `Invalid PlaceRef key in HotelAvailabilityResponse: "${key}"`,
                data
              )
              return
            }
            if (!value || typeof value !== "object") {
              console.error(
                `Invalid HotelAvailability value for key "${key}": value is not an object`,
                value
              )
              return
            }
          }

          // Type assertion after validation
          const validatedData = data as HotelAvailabilityResponse

          // Populate individual caches from batch response
          ;(
            Object.entries(validatedData) as [PlaceRef, HotelAvailability][]
          ).forEach(([placeRef, availability]) => {
            void dispatch(
              hotelApi.util.upsertQueryData(
                "getHotelAvailability",
                {
                  placeRefs: [placeRef],
                  travelers: arg.travelers,
                  room: arg.room,
                  start: arg.start,
                  end: arg.end,
                  currency: arg.currency,
                },
                { [placeRef]: availability } as HotelAvailabilityResponse
              )
            )
          })
        } catch (error) {
          console.error(error)
        }
      },
      keepUnusedDataFor: 900, // 15 minutes
    }),
    getRankedHotelAvailability: builder.query<
      RankedHotelAvailabilityResult,
      GetRankedHotelAvailabilityArgs
    >({
      query: ({
        placeRef,
        selectedRoomId,
        hotelQuoteRef,
        travelers,
        childAges,
        start,
        end,
        currency,
        recoveryToken: _recoveryToken,
        priceAlertIntent,
      }) => {
        const postData = hotelQuoteRef
          ? {
              entity_ref: placeRef,
              hotel_quote_ref: hotelQuoteRef,
              ...(travelers
                ? {
                    adult_count: travelers.adult_count,
                    child_count: travelers.child_count,
                    infant_count: travelers.infant_count,
                  }
                : {}),
              ...(childAges ? { child_ages: childAges } : {}),
              ...(start ? { check_in: start } : {}),
              ...(end ? { check_out: end } : {}),
              ...(currency ? { currency } : {}),
              ...(priceAlertIntent
                ? { price_alert_intent: priceAlertIntent }
                : {}),
            }
          : {
              entity_ref: placeRef,
              selected_room_id: selectedRoomId,
              adult_count: travelers?.adult_count,
              child_count: travelers?.child_count,
              infant_count: travelers?.infant_count,
              child_ages: childAges,
              check_in: start,
              check_out: end,
              ...(currency ? { currency } : {}),
              ...(priceAlertIntent
                ? { price_alert_intent: priceAlertIntent }
                : {}),
            }
        return {
          url: "/api/hotels/ranked_availability",
          method: "post",
          data: postData,
        }
      },
      transformResponse: (
        response: RankedHotelAvailabilityResponse,
        _meta,
        { placeRef }
      ) => ({
        availability: response.results[placeRef],
        check_in: response.check_in,
        check_out: response.check_out,
        adults: response.adults,
        children: response.children,
        child_ages: response.child_ages,
        rooms: response.rooms,
        suggestion_reason: response.suggestion_reason,
      }),
      providesTags: (
        _result,
        _error,
        { placeRef, selectedRoomId, hotelQuoteRef }
      ) => [
        {
          type: "RankedHotelAvailability" as const,
          id: `${placeRef}-${hotelQuoteRef ?? selectedRoomId}`,
        },
      ],
      keepUnusedDataFor: 900, // 15 minutes
    }),
  }),
})

export const {
  useGetHotelAvailabilityQuery,
  useLazyGetHotelAvailabilityQuery,
  useGetRankedHotelAvailabilityQuery,
  useLazyGetRankedHotelAvailabilityQuery,
} = hotelApi

/**
 * Drops every cached hotel availability entry.
 *
 * Eviction rather than invalidation: recovery runs precisely because the offer
 * on screen is gone, and an invalidated entry keeps serving its old price until
 * the refetch lands. Back when hotels had their own api a `resetApiState()`
 * reached exactly these entries; with one api for the whole app it would reach
 * every cache in the store, so the entries are named by their tags instead.
 */
export const evictHotelAvailability =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const cached = baseApi.util.selectInvalidatedBy(getState(), [
      "HotelAvailability",
      "RankedHotelAvailability",
    ])
    for (const { queryCacheKey } of cached) {
      // RTK hands out a plain `string` here but brands the key it wants back,
      // so the two halves of its own api do not line up without this.
      dispatch(
        baseApi.internalActions.removeQueryResult({
          queryCacheKey: queryCacheKey as QueryCacheKey,
        })
      )
    }
  }
